import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useForm, Controller } from "react-hook-form"
import { toast } from "react-toastify"
import { useUpdatePhoneNumberMutation } from "../../../../../../service/user-service"
import { PhoneInput } from "../../../../components"
import styles from "../edit-account-name-modal/edit-account-name-modal.module.scss"
import { validatePhoneNumber } from "../../../../validators"
import { useTenant } from "../../../../../../tenant/branding/useTenant"

interface EditPhoneNumberModalProps {
  isOpen: boolean
  onClose: () => void
  onOtpSent: (phoneNumber: string) => void
  phoneNumber: string
}

interface FormValues {
  phoneNumber: string
}

export const EditPhoneNumberModal = ({
  isOpen,
  onClose,
  onOtpSent,
  phoneNumber
}: EditPhoneNumberModalProps) => {
  const { t } = useTranslation()
  const { countryCode } = useTenant()
  const [updatePhoneNumber, { isLoading: isSendingOtp }] = useUpdatePhoneNumberMutation()

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      phoneNumber: ""
    }
  })

  useEffect(() => {
    if (isOpen) {
      reset({ phoneNumber })
    }
  }, [isOpen, phoneNumber, reset])

  const handleCancel = () => {
    reset({ phoneNumber })
    onClose()
  }

  const onSubmit = async (data: FormValues) => {
      try {
      await updatePhoneNumber({
        phoneNumber: data.phoneNumber
      }).unwrap()

      toast.success(t("account.web.otpSentToPhoneNumber"))
      onOtpSent(data.phoneNumber)
      onClose()
    } catch (error: any) {
      toast.error(error?.data?.title || t("account.web.failedToSendOtp"))
    }
  }

  if (!isOpen) return null

  return (
    <div className={styles.modalOverlay} onClick={handleCancel}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.toolbar}>
          <button
            type="button"
            className={styles.closeButton}
            onClick={handleCancel}
            disabled={isSendingOtp}
            aria-label={t("account.web.close")}
          >
            ×
          </button>
          <h2 className={styles.modalTitle}>{t("common.labels.editPhoneNumber")}</h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.body}>
            <div className={styles.fieldsCard}>
              <div className={styles.inputField}>
                <label className={styles.inputLabel}>{t("common.labels.phoneNumber")}</label>
                <Controller
                  name="phoneNumber"
                  control={control}
                  rules={{
                    required: t("errors.api.phoneNumberRequired"),
                    validate: validatePhoneNumber,
                  }}
                  render={({ field, fieldState }) => (
                    <PhoneInput
                      value={field.value}
                      onChange={(phone) => field.onChange(phone)}
                      defaultCountry={countryCode}
                      lockCountry
                      placeholder={t("account.web.enterPhoneNumber")}
                      disabled={isSendingOtp}
                      error={fieldState.error?.message}
                    />
                  )}
                />
              </div>
            </div>

            <button
              type="submit"
              className={styles.saveButton}
              disabled={isSendingOtp}
            >
              {isSendingOtp ? t("account.web.sending") : t("account.saveChanges")}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
